import React, { useState } from "react";
import { useParams } from 'react-router-dom';
import Header from './Header.jsx'
import Contact from './Contact.jsx'
import Footer from './Footer.jsx'
import AppHead from './AppHead.jsx'
import { motion, AnimatePresence } from 'framer-motion'
import { 
    Share2, MessageCircle, CreditCard, User, HelpCircle, ArrowLeft, Search, 
    CheckCircle, AlertCircle, FileText, RefreshCw, ThumbsUp, XCircle, 
    Clock, MapPin, Phone, Mail, ExternalLink, ChevronDown, ChevronUp,
    BookOpen, Shield, Zap, Star, TrendingUp, Users, Calendar, Globe,
    Ticket, Settings
} from 'lucide-react'

const HelpCenterInfo = () => {
    let { name } = useParams();
    const [expandedSections, setExpandedSections] = useState({});
    const [activeTab, setActiveTab] = useState('overview');
    
    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };
    
    const helpTopics = [
        {
            slug: "managing-your-account",
            title: "Managing Your Account",
            icon: <User className="w-6 h-6 text-white!" />,
            color: "from-orange-400 to-orange-500",
            gradient: "from-orange-50 to-orange-100",
            borderColor: "border-orange-200",
            textColor: "text-orange-900",
            description: "Learn how to manage your profile, security settings, and preferences",
            content: {
                overview: {
                    title: "Account Management Overview",
                    description: "Your EventPlots account is your gateway to discovering and booking amazing events. Here's everything you need to know about managing it effectively.",
                    features: [
                        { icon: <User className="w-4 h-4" />, text: "Profile customization and personalization" },
                        { icon: <Shield className="w-4 h-4" />, text: "Security settings and password management" },
                        { icon: <MessageCircle className="w-4 h-4" />, text: "Notification preferences and subscriptions" },
                        { icon: <Ticket className="w-4 h-4" />, text: "Purchase history and ticket management" }
                    ]
                },
                sections: [
                    {
                        id: "profile",
                        title: "Profile Management",
                        icon: <User className="w-5 h-5" />,
                        content: (
                            <div className="space-y-4">
                                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                    <h5 className="font-semibold text-gray-900 mb-2">Update Personal Information</h5>
                                    <p className="text-gray-600 text-sm mb-3">Keep your profile up-to-date to receive personalized event recommendations and ensure smooth ticket purchases.</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4 text-orange-500" />
                                            <span>Name and contact details</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4 text-orange-500" />
                                            <span>Profile picture upload</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4 text-orange-500" />
                                            <span>Location preferences</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4 text-orange-500" />
                                            <span>Event category interests</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    },
                    {
                        id: "security",
                        title: "Account Security",
                        icon: <Shield className="w-5 h-5" />,
                        content: (
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                                    <h5 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                                        <Shield className="w-4 h-4" />
                                        Security Best Practices
                                    </h5>
                                    <ul className="text-orange-800 text-sm space-y-2">
                                        <li className="flex items-start gap-2">
                                            <CheckCircle className="w-3 h-3 mt-0.5 text-orange-600" />
                                            <span>Use a strong, unique password</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircle className="w-3 h-3 mt-0.5 text-orange-600" />
                                            <span>Never share your login credentials</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircle className="w-3 h-3 mt-0.5 text-orange-600" />
                                            <span>Log out from shared devices</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircle className="w-3 h-3 mt-0.5 text-orange-600" />
                                            <span>Keep your contact information updated</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )
                    }
                ]
            }
        },
        {
            slug: "booking-tickets",
            title: "Booking Tickets",
            icon: <Ticket className="w-6 h-6 text-white!" />,
            color: "from-teal-400 to-teal-500",
            gradient: "from-teal-50 to-teal-100",
            borderColor: "border-teal-200",
            textColor: "text-teal-900",
            description: "Complete guide to finding and booking events on EventPlots",
            content: {
                overview: {
                    title: "Ticket Booking Process",
                    description: "Booking tickets on EventPlots is simple and secure. Follow these steps to ensure a smooth experience.",
                    features: [
                        { icon: <Search className="w-4 h-4" />, text: "Advanced search and filtering options" },
                        { icon: <CreditCard className="w-4 h-4" />, text: "Secure payment processing" },
                        { icon: <CheckCircle className="w-4 h-4" />, text: "Instant confirmation and e-tickets" },
                        { icon: <Users className="w-4 h-4" />, text: "Group booking capabilities" }
                    ]
                },
                sections: [
                    {
                        id: "search",
                        title: "Finding Events",
                        icon: <Search className="w-5 h-5" />,
                        content: (
                            <div className="space-y-4">
                                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                    <h5 className="font-semibold text-gray-900 mb-3">Search and Discovery</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <h6 className="font-medium text-gray-800 mb-2">Search Options</h6>
                                            <ul className="text-sm text-gray-600 space-y-1">
                                                <li>• Keyword search</li>
                                                <li>• Category browsing</li>
                                                <li>• Location-based search</li>
                                                <li>• Date range filtering</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h6 className="font-medium text-gray-800 mb-2">Advanced Filters</h6>
                                            <ul className="text-sm text-gray-600 space-y-1">
                                                <li>• Price range</li>
                                                <li>• Event type</li>
                                                <li>• Age restrictions</li>
                                                <li>• Accessibility features</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    },
                    {
                        id: "selection",
                        title: "Ticket Selection",
                        icon: <Ticket className="w-5 h-5" />,
                        content: (
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl p-4 border border-teal-200">
                                    <h5 className="font-semibold text-teal-900 mb-3">Choosing the Right Tickets</h5>
                                    <div className="space-y-3">
                                        <div className="flex items-start gap-3">
                                            <div className="bg-teal-100 rounded-full p-1 mt-0.5">
                                                <CheckCircle className="w-3 h-3 text-teal-600" />
                                            </div>
                                            <div>
                                                <h6 className="font-medium text-teal-900 text-sm">Review Event Details</h6>
                                                <p className="text-teal-800 text-xs">Check date, time, location, and any special requirements</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="bg-teal-100 rounded-full p-1 mt-0.5">
                                                <CheckCircle className="w-3 h-3 text-teal-600" />
                                            </div>
                                            <div>
                                                <h6 className="font-medium text-teal-900 text-sm">Compare Ticket Types</h6>
                                                <p className="text-teal-800 text-xs">VIP, General Admission, Early Bird, etc.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="bg-teal-100 rounded-full p-1 mt-0.5">
                                                <CheckCircle className="w-3 h-3 text-teal-600" />
                                            </div>
                                            <div>
                                                <h6 className="font-medium text-teal-900 text-sm">Check Availability</h6>
                                                <p className="text-teal-800 text-xs">Ensure tickets are available for your preferred date</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                ]
            }
        },
        {
            slug: "payment-options",
            title: "Payment Options",
            icon: <CreditCard className="w-6 h-6 text-white!" />,
            color: "from-orange-400 to-orange-500",
            gradient: "from-orange-50 to-orange-100",
            borderColor: "border-orange-200",
            textColor: "text-orange-900",
            description: "All payment methods and transaction security information",
            content: {
                overview: {
                    title: "Payment Methods & Security",
                    description: "EventPlots offers multiple secure payment options to make booking tickets convenient and safe.",
                    features: [
                        { icon: <CreditCard className="w-4 h-4" />, text: "Credit/debit card payments" },
                        { icon: <Phone className="w-4 h-4" />, text: "Mobile money integration" },
                        { icon: <Shield className="w-4 h-4" />, text: "Bank-level security encryption" },
                        { icon: <FileText className="w-4 h-4" />, text: "Detailed transaction records" }
                    ]
                },
                sections: [
                    {
                        id: "methods",
                        title: "Accepted Payment Methods",
                        icon: <CreditCard className="w-5 h-5" />,
                        content: (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                        <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                            <CreditCard className="w-4 h-4 text-orange-600" />
                                            Cards
                                        </h5>
                                        <ul className="text-sm text-gray-600 space-y-1">
                                            <li>• Visa</li>
                                            <li>• Mastercard</li>
                                            <li>• American Express</li>
                                            <li>• Discover</li>
                                        </ul>
                                    </div>
                                    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                        <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-teal-600" />
                                            Mobile Money
                                        </h5>
                                        <ul className="text-sm text-gray-600 space-y-1">
                                            <li>• MTN Mobile Money</li>
                                            <li>• Airtel Money</li>
                                            <li>• M-Pesa</li>
                                            <li>• Other local providers</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                ]
            }
        },
        {
            slug: "refunds-and-cancellations",
            title: "Refunds and Cancellations",
            icon: <RefreshCw className="w-6 h-6 text-white!" />,
            color: "from-teal-400 to-teal-500",
            gradient: "from-teal-50 to-teal-100",
            borderColor: "border-teal-200",
            textColor: "text-teal-900",
            description: "Understanding refund policies and cancellation procedures",
            content: {
                overview: {
                    title: "Refund & Cancellation Policies",
                    description: "We understand that plans can change. Learn about our flexible refund and cancellation policies.",
                    features: [
                        { icon: <Clock className="w-4 h-4" />, text: "Flexible refund windows" },
                        { icon: <RefreshCw className="w-4 h-4" />, text: "Easy cancellation process" },
                        { icon: <CheckCircle className="w-4 h-4" />, text: "Automatic refunds for cancellations" },
                        { icon: <FileText className="w-4 h-4" />, text: "Clear policy documentation" }
                    ]
                },
                sections: [
                    {
                        id: "eligibility",
                        title: "Refund Eligibility",
                        icon: <CheckCircle className="w-5 h-5" />,
                        content: (
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl p-4 border border-teal-200">
                                    <h5 className="font-semibold text-teal-900 mb-3">When Refunds Are Available</h5>
                                    <div className="space-y-3">
                                        <div className="flex items-start gap-3">
                                            <div className="bg-teal-100 rounded-full p-1 mt-0.5">
                                                <CheckCircle className="w-3 h-3 text-teal-600" />
                                            </div>
                                            <div>
                                                <h6 className="font-medium text-teal-900 text-sm">Event Cancellation</h6>
                                                <p className="text-teal-800 text-xs">Full refund for events canceled by organizers</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="bg-teal-100 rounded-full p-1 mt-0.5">
                                                <CheckCircle className="w-3 h-3 text-teal-600" />
                                            </div>
                                            <div>
                                                <h6 className="font-medium text-teal-900 text-sm">Within Refund Window</h6>
                                                <p className="text-teal-800 text-xs">Refunds available up to specified time before event</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="bg-teal-100 rounded-full p-1 mt-0.5">
                                                <CheckCircle className="w-3 h-3 text-teal-600" />
                                            </div>
                                            <div>
                                                <h6 className="font-medium text-teal-900 text-sm">Technical Issues</h6>
                                                <p className="text-teal-800 text-xs">Refunds for payment or booking errors</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                ]
            }
        },
        {
            slug: "customer-support",
            title: "Customer Support",
            icon: <MessageCircle className="w-6 h-6 text-white!" />,
            color: "from-orange-400 to-orange-500",
            gradient: "from-orange-50 to-orange-100",
            borderColor: "border-orange-200",
            textColor: "text-orange-900",
            description: "Get help when you need it - multiple support channels available",
            content: {
                overview: {
                    title: "24/7 Customer Support",
                    description: "Our dedicated support team is here to help you with any questions or issues you may have.",
                    features: [
                        { icon: <MessageCircle className="w-4 h-4" />, text: "Live chat support" },
                        { icon: <Phone className="w-4 h-4" />, text: "Phone support hotline" },
                        { icon: <Mail className="w-4 h-4" />, text: "Email support" },
                        { icon: <HelpCircle className="w-4 h-4" />, text: "Comprehensive FAQ section" }
                    ]
                },
                sections: [
                    {
                        id: "contact",
                        title: "Contact Information",
                        icon: <Phone className="w-5 h-5" />,
                        content: (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                        <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                            <Phone className="w-4 h-4 text-orange-600" />
                                            Phone Support
                                        </h5>
                                        <p className="text-sm text-gray-600 mb-2">Available 24/7</p>
                                        <p className="text-lg font-semibold text-orange-600">+256 200 905 932</p>
                                    </div>
                                    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                        <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                            <Mail className="w-4 h-4 text-teal-600" />
                                            Email Support
                                        </h5>
                                        <p className="text-sm text-gray-600 mb-2">Response within 2 hours</p>
                                        <p className="text-lg font-semibold text-teal-600">support@eventplots.com</p>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                ]
            }
        }
    ];

    const topic = helpTopics.find(t => t.slug === name) || {
        title: "Help Topic Not Found",
        icon: <HelpCircle className="w-6 h-6" />,
        color: "from-gray-500 to-gray-600",
        gradient: "from-gray-50 to-gray-100",
        borderColor: "border-gray-200",
        textColor: "text-gray-900",
        description: "The requested help topic could not be found",
        content: {
            overview: {
                title: "Topic Not Found",
                description: "We couldn't find the help topic you're looking for.",
                features: []
            },
            sections: []
        }
    };

    // Social sharing links
    const shareUrl = window.location.href;
    const shareTitle = topic.title;
    const shareDescription = "Learn more about using EventPlots in this comprehensive help article!";
    const whatsappShare = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareTitle} - ${shareDescription} ${shareUrl}`)}`;
    const facebookShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&t=${encodeURIComponent(shareTitle)}`;
    const twitterShare = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${shareTitle} - ${shareDescription}`)}&url=${encodeURIComponent(shareUrl)}`;

    const tabs = [
        { id: 'overview', label: 'Overview', icon: <BookOpen className="w-4 h-4" /> },
        { id: 'details', label: 'Details', icon: <FileText className="w-4 h-4" /> },
        { id: 'faq', label: 'FAQ', icon: <HelpCircle className="w-4 h-4" /> }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
            <Header />
            <AppHead
                title={`${topic.title} - EventPlots Help Center`}
                description={topic.description}
                imageUrl="https://eventplots.com/images/logo.png"
                url={window.location.href}
                twitterCardType="summary_large_image"
            />
            
            <div className="container mx-auto px-4 py-8 max-w-6xl">
                {/* Breadcrumb */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-6"
                >
                    <a 
                        href="/help-center" 
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors duration-200 group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        <span className="text-sm font-medium">Back to Help Center</span>
                    </a>
                </motion.div>

                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className={`bg-gradient-to-r ${topic.color} rounded-3xl p-8 text-white mb-8 shadow-xl`}
                >
                    <div className="flex items-start gap-6">
                        <div className="bg-white/20 rounded-2xl p-4 backdrop-blur-sm">
                            {topic.icon}
                        </div>
                        <div className="flex-1">
                            <h1 className="text-xl font-bold mb-3 text-white!">{topic.title}</h1>
                            <p className="text-white/90! text-lg leading-relaxed max-w-2xl">
                                {topic.description}
                            </p>
                            <div className="flex items-center gap-4 mt-6">
                                <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 backdrop-blur-sm">
                                    <Clock className="w-4 h-4 text-white!" />
                                    <span className="text-sm text-white!">5 min read</span>
                                </div>
                                <div className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 backdrop-blur-sm">
                                    <Star className="w-4 h-4 text-white!" />
                                    <span className="text-sm text-white!">Last updated: Dec 2024</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="sticky top-8 z-0"
                        >
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <h3 className="font-semibold text-gray-900 mb-4">Quick Navigation</h3>
                                <nav className="space-y-2">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`w-full flex gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                                activeTab === tab.id
                                                    ? `bg-gradient-to-r ${topic.color} text-white! shadow-md`
                                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                            }`}
                                        >
                                            {tab.icon}
                                            {tab.label}
                                        </button>
                                    ))}
                                </nav>
                                
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h4 className="font-semibold text-gray-900 mb-3">Related Topics</h4>
                                    <div className="space-y-2">
                                        {helpTopics.slice(0, 3).map((relatedTopic) => (
                                            <a
                                                key={relatedTopic.slug}
                                                href={`/help-center/${relatedTopic.slug}`}
                                                className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-600 transition-colors duration-200"
                                            >
                                                {relatedTopic.icon}
                                                <span>{relatedTopic.title}</span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Main Content Area */}
                    <div className="lg:col-span-3">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
                        >
                            {/* Tab Content */}
                            <AnimatePresence mode="wait">
                                {activeTab === 'overview' && (
                                    <motion.div
                                        key="overview"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.3 }}
                                        className="p-8"
                                    >
                                        <div className="mb-8">
                                            <h2 className="text-lg font-bold text-gray-900 mb-4">
                                                {topic.content.overview.title}
                                            </h2>
                                            <p className="text-gray-600 leading-relaxed mb-6">
                                                {topic.content.overview.description}
                                            </p>
                                            
                                            {topic.content.overview.features.length > 0 && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {topic.content.overview.features.map((feature, index) => (
                                                        <motion.div
                                                            key={index}
                                                            initial={{ opacity: 0, y: 20 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            transition={{ duration: 0.4, delay: index * 0.1 }}
                                                            className={`bg-gradient-to-r ${topic.gradient} rounded-xl p-4 border ${topic.borderColor}`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className={`bg-white/50 rounded-lg p-2 ${topic.textColor}`}>
                                                                    {feature.icon}
                                                                </div>
                                                                <span className="font-medium text-gray-900">{feature.text}</span>
                                                            </div>
                                                        </motion.div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}

                                {activeTab === 'details' && (
                                    <motion.div
                                        key="details"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.3 }}
                                        className="p-8"
                                    >
                                        <h2 className="text-lg font-bold text-gray-900 mb-6">Detailed Information</h2>
                                        <div className="space-y-6">
                                            {topic.content.sections.map((section, index) => (
                                                <motion.div
                                                    key={section.id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                                    className="border border-gray-200 rounded-xl overflow-hidden"
                                                >
                                                    <button
                                                        onClick={() => toggleSection(section.id)}
                                                        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className={`${topic.textColor}`}>
                                                                {section.icon}
                                                            </div>
                                                            <h3 className="font-semibold text-gray-900">{section.title}</h3>
                                                        </div>
                                                        {expandedSections[section.id] ? (
                                                            <ChevronUp className="w-5 h-5 text-gray-500" />
                                                        ) : (
                                                            <ChevronDown className="w-5 h-5 text-gray-500" />
                                                        )}
                                                    </button>
                                                    <AnimatePresence>
                                                        {expandedSections[section.id] && (
                                                            <motion.div
                                                                initial={{ opacity: 0, height: 0 }}
                                                                animate={{ opacity: 1, height: "auto" }}
                                                                exit={{ opacity: 0, height: 0 }}
                                                                transition={{ duration: 0.3 }}
                                                                className="p-4 bg-white"
                                                            >
                                                                {section.content}
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}

                                {activeTab === 'faq' && (
                                    <motion.div
                                        key="faq"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.3 }}
                                        className="p-8"
                                    >
                                        <h2 className="text-lg font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
                                        <div className="space-y-4">
                                            <div className="bg-gray-50 rounded-xl p-6">
                                                <h3 className="font-semibold text-gray-900 mb-2">How do I contact support?</h3>
                                                <p className="text-gray-600 text-sm">You can reach our support team via phone (+256 200 905 932), email (support@eventplots.com), or live chat. We're available 24/7 to help with any questions or issues.</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-xl p-6">
                                                <h3 className="font-semibold text-gray-900 mb-2">What payment methods are accepted?</h3>
                                                <p className="text-gray-600 text-sm">We accept major credit cards (Visa, Mastercard, American Express), mobile money (MTN, Airtel, M-Pesa), and bank transfers. All transactions are secured with encryption.</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-xl p-6">
                                                <h3 className="font-semibold text-gray-900 mb-2">Can I get a refund?</h3>
                                                <p className="text-gray-600 text-sm">Refunds are available for canceled events or within the specified refund window. Check the event details for specific policies. Contact support for assistance with refund requests.</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                        
                        {/* Share Section */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="mt-8 bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
                        >
                            <div className="flex items-center gap-2 mb-4">
                                <Share2 className="w-5 h-5 text-gray-600" />
                                <h4 className="text-lg font-semibold text-gray-900">Share This Article</h4>
                            </div>
                            
                            <div className="flex flex-wrap gap-3">
                                <a 
                                    href={whatsappShare} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white! px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 font-medium"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                    </svg>
                                    Share on WhatsApp
                                </a>
                                
                                <a 
                                    href={facebookShare} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-blue-600! hover:bg-blue-700! text-white! px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 font-medium"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                    </svg>
                                    Share on Facebook
                                </a>
                                
                                <a 
                                    href={twitterShare} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white! px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 font-medium"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                                    </svg>
                                    Share on Twitter
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
            
            <Contact />
            <Footer />
        </div>
    )
}

export default HelpCenterInfo